<template>
    <div id="add-user">
        <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
                <v-btn :color="primaryColor" dark small v-bind="attrs" v-on="on" @click.stop="openAddUserDialog()">
                    <v-icon>mdi-account-plus</v-icon>
                </v-btn>
            </template>
            <span>Add User</span>
        </v-tooltip>
        <v-dialog v-model="addUserDialog" max-width="767" persistent>
            <v-card ref="addUserCard">
                <v-card-title>
                    Add Residence Assistant
                    <v-spacer></v-spacer>
                    <residence-assistant-manager-lookup-user @selectUser="selectUser"
                        @setNotification="setNotification">
                    </residence-assistant-manager-lookup-user>
                </v-card-title>
                <v-card-text class="pb-0">
                    <v-form ref="addUserForm" v-model="valid" lazy-validation>
                        <v-text-field v-model="currentUser.cn" :disabled="readOnly" label="Name" :rules="nameRules"
                            required>
                        </v-text-field>
                        <v-text-field v-model="currentUser.username" counter="8"
                            hint="Please provide only the Western ID. Do not include @uwo.ca" :disabled="readOnly"
                            label="Western Mail" persistent-hint :rules="mailRules" suffix="@uwo.ca" required>
                        </v-text-field>
                        <v-select v-model="currentUser.gender" :items="genderItems" label="Gender"
                            placeholder="Please select..." required :rules="genderRules">
                        </v-select>
                        <v-text-field v-model="currentUser.prefpronoun" label="Preferred Pronoun" :rules="pronounRules">
                        </v-text-field>
                        <v-select v-model="currentUser.ratype" :items="raItems" label="Residence Assistant Type"
                            :rules="raRules" required>
                        </v-select>
                        <v-select v-model="currentUser.building" hide-details :items="getBuildings" label="Building">
                        </v-select>
                        <v-text-field v-model="currentUser.room" hide-details label="Room">
                        </v-text-field>
                        <v-container fluid>
                            <v-row>
                                <v-col cols="12" md="6" class="py-0">
                                    <v-menu ref="startDate" v-model="startDateMenu" :close-on-content-click="false"
                                        transition="scale-transition" offset-y max-width="290px" min-width="290px">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-text-field v-model="currentUser.startDate" id="startDate"
                                                :color="successColor" label="Start Date" name="startDate"
                                                prepend-icon="mdi-calendar-check" readonly :rules="startDateRules"
                                                v-bind="attrs" v-on="on">
                                            </v-text-field>
                                        </template>
                                        <v-date-picker v-model="currentUser.startDate" :color="successColor"
                                            :max="currentUser.endDate" no-title @input="startDateMenu = false">
                                        </v-date-picker>
                                    </v-menu>
                                </v-col>
                                <v-col cols="12" md="6" class="py-0">
                                    <v-menu ref="endDate" v-model="endDateMenu" :close-on-content-click="false"
                                        transition="scale-transition" offset-y max-width="290px" min-width="290px">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-text-field v-model="currentUser.endDate" id="endDate"
                                                :color="dangerColor" label="End Date" name="endDate"
                                                prepend-icon="mdi-calendar-remove" readonly :rules="endDateRules"
                                                v-bind="attrs" v-on="on">
                                            </v-text-field>
                                        </template>
                                        <v-date-picker v-model="currentUser.endDate" :color="dangerColor"
                                            :min="currentUser.startDate" no-title @input="endDateMenu = false">
                                        </v-date-picker>
                                    </v-menu>
                                </v-col>
                                <v-col cols="12" class="pt-0">
                                    <v-checkbox v-model="currentUser.active" dense false-value="0"
                                        hint="This active control will override the start date and end date"
                                        label="Active" persistent-hint true-value="1"></v-checkbox>
                                </v-col>
                            </v-row>
                        </v-container>
                    </v-form>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn ref="addBtn" class="white--text" :color="successColor" :disabled="!valid"
                        @click="openAddUserConfirmationDialog()">
                        Add
                    </v-btn>
                    <v-btn ref="resetBtn" class="white--text" :color="primaryColor" @click="resetForm()">
                        Reset
                    </v-btn>
                    <v-btn ref="closeBtn" :color="warningColor" @click="closeAddUserDialog()">
                        Cancel
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-dialog v-model="addUserConfirmationDialog" max-width="540">
            <v-card ref="addUserConfirmationCard">
                <v-card-title>Confirmation</v-card-title>
                <v-card-text>
                    Are you sure you would like to add {{ currentUser.cn }} as a Residence Assistant? Please click
                    confirm to proceed.
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn ref="confirmBtn" :color="dangerColor" text @click="addUser()">
                        Confirm
                    </v-btn>
                    <v-btn :color="warningColor" text @click="closeAddUserConfirmationDialog()">
                        Cancel
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import ResidenceAssistantManagerLookupUser from './LookupUser';

export default {
    name: 'residence-assistant-manager-add-user',
    components: {
        ResidenceAssistantManagerLookupUser
    },
    props: {
        buildings: {
            required: true,
            type: Array
        }
    },
    data() {
        return {
            addUserConfirmationDialog: false,
            addUserDialog: false,
            currentUser: {
                active: '0',
                building: '',
                endDate: '',
                ratype: '',
                room: '',
                startDate: ''
            },
            endDateMenu: false,
            endDateRules: [
                v => !!v || 'End date is required',
            ],
            genderItems: [{
                    text: 'Female',
                    value: 'F'
                },
                {
                    text: 'Male',
                    value: 'M'
                },
                {
                    text: 'Non-Binary',
                    value: 'NonBinary'
                }
            ],
            genderRules: [
                v => !!v || 'Gender is required'
            ],
            pronounRules: [
                v => !!v || 'Pronoun is required',
                v => /^([A-Za-z]+)\/([A-Za-z]+)$/.test(v) || 'Pronoun should include forward slash (/)',
            ],
            mailRules: [
                v => !!v || 'Western mail is required',
                v => (v && v.length >= 2 && v.length <= 8) || 'Western mail must be valid',
            ],
            nameRules: [
                v => !!v || 'Name is required',
                v => (v && v.length >= 2) || 'Name must be more than 2 characters',
            ],
            raItems: [{
                    disabled: true,
                    text: 'Please select...',
                    value: ''
                }, {
                    text: 'Residence Assistant',
                    value: 'residence assistant'
                },
                {
                    text: 'Head Residence Assistant',
                    value: 'head residence assistant'
                },
                {
                    text: 'House Coordinator',
                    value: 'house coordinator'
                }
            ],
            raRules: [
                v => !!v || 'Residence Assistant type is required'
            ],
            readOnly: false,
            startDateMenu: false,
            startDateRules: [
                v => !!v || 'Start date is required',
            ],
            valid: true
        }
    },
    methods: {
        addUser() {
            const that = this;
            const formData = new FormData();
            let api = `https://${location.hostname}/cgi-bin/res-app-api/controller.api.pl?setRA`;
            let user = this.currentUser;

            // Set required keys
            user.building = this.currentUser.building || "";
            user.mail = `${user.username}@uwo.ca`;
            user.room = this.currentUser.room || "";
            this.$refs.confirmBtn.loading = true;

            // Parse array into string
            formData.append('mode', 'setRA');
            formData.append('data', JSON.stringify(user));

            // Post request
            fetch(api, {
                    method: 'POST',
                    body: formData
                })
                .then(function (response) {
                    // Return JSON response
                    return response.json();
                })
                .then(function (json) {
                    // Success result
                    if (json.result.match(/OK/i)) {
                        that.$emit('setNotification', '#1dc9b7', 'mdi-account-check', `${user.cn} has been successfully added as a Residence Assistant.`, true);
                    } else if (json.message.match(/UNAUTHORIZED/i)) {
                        that.$emit('setNotification', '#f64e60', 'mdi-alert-circle', `You are not authorized to perform this action. Please refresh your application.`, true);
                    } else {
                        that.$emit('setNotification', '#ffb822', 'mdi-account-alert', `${user.cn} could not be added as a Residence Assistant. Please contact your system administrator.`, true);
                    }

                    that.$emit('refreshData');
                    that.$refs.confirmBtn.loading = false;
                    that.closeAddUserConfirmationDialog();
                    that.closeAddUserDialog();
                })
                .catch(function () {
                    that.$emit('refreshData');
                    that.$refs.confirmBtn.loading = false;
                    that.$emit('setNotification', '#f64e60', 'mdi-alert-circle', `An error occurred. Please contact your system administrator.`, true);
                });
        },
        closeAddUserConfirmationDialog() {
            this.addUserConfirmationDialog = false;
        },
        closeAddUserDialog() {
            this.addUserDialog = false;
        },
        openAddUserConfirmationDialog() {
            if (this.$refs.addUserForm.validate()) {
                this.addUserConfirmationDialog = true;
            }
        },
        openAddUserDialog() {
            // Reset form
            this.resetForm();
            this.addUserDialog = true;
        },
        resetForm() {
            // Reset validation
            this.resetFormValidation();
            this.currentUser = {
                active: '0',
                cn: '',
                building: '',
                endDate: '',
                ratype: '',
                room: '',
                startDate: '',
                username: ''
            };
            this.readOnly = false;
        },
        resetFormValidation() {
            if (this.$refs.addUserForm) {
                this.$refs.addUserForm.resetValidation();
            }
        },
        selectUser(user) {
            if (user) {
                this.readOnly = true;
                this.currentUser = user;
                this.currentUser.building = '';
                this.currentUser.room = '';
                this.$emit('setNotification', '#5578eb', 'mdi-account-check',
                    `The user ${this.currentUser.cn} has been selected.`,
                    true);
            } else {
                this.$emit('setNotification', '#ffb822', 'mdi-account-alert',
                    `The user could not be selected. The user may no longer exist. Please try again.`, true);
            }

            // Reset form validation
            this.resetFormValidation();
        },
        setNotification(color, icon, message, status) {
            this.$emit('setNotification', color, icon, message, status);
        }
    },
    computed: {
        dangerColor() {
            return '#f64e60';
        },
        getBuildings() {
            let buildingsList = [];

            // Push empty buildings value
            buildingsList.push({
                text: 'None',
                value: ''
            });

            // Parse the buildings array
            for (let i = 0; i < this.buildings.length; i++) {
                if (this.buildings[i].name != "_buildingsInit") {
                    buildingsList.push({
                        text: this.buildings[i].name,
                        value: this.buildings[i].name
                    });
                }
            }

            // Sort array
            buildingsList.sort((a, b) => a.value.localeCompare(b.value));

            return buildingsList;
        },
        primaryColor() {
            return '#5867dd';
        },
        successColor() {
            return '#1dc9b7';
        },
        warningColor() {
            return '#ffb822'
        }
    }
}
</script>

<style scoped>
.v-input--checkbox {
    margin-top: 0;
}
</style>